import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase.js";
import { rentalDays, autoPrice, rentalTotal } from "../lib/rental-time.js";

/**
 * Website booking requests.
 *
 * Rows arrive from halcoequipmentrentals.com via the anon key, which may insert
 * here and read nothing back. Staff triage them into real rentals.
 */

const SELECT =
  "id,equipment_id,start_date,start_time,end_date,end_time,name,business," +
  "phone,email,notes,processed,created_at,equipment(id,name,day_rate,week_rate,month_rate)";

export function useBookingRequests() {
  return useQuery({
    queryKey: ["booking_requests"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("booking_requests")
        .select(SELECT)
        .order("created_at", { ascending: false });
      if (error) throw new Error(error.message);
      return data;
    },
    // Requests arrive while the app is open, so poll rather than waiting for a
    // reload. Cheap: this table stays small because accepted rows are archived.
    refetchInterval: 60_000,
  });
}

export function usePendingCount() {
  const q = useBookingRequests();
  return (q.data ?? []).filter((r) => !r.processed).length;
}

/** Reuse of the invoice-number claim from rentals — see the note there. */
async function claimInvoiceNumber(attempt = 0) {
  if (attempt >= 5) {
    throw new Error("Could not reserve an invoice number — too many simultaneous saves. Try again.");
  }
  const { data: current, error: readErr } = await supabase
    .from("settings").select("invoice_seq").eq("id", 1).single();
  if (readErr) throw new Error(`Could not read the invoice counter: ${readErr.message}`);

  const seq = Number(current.invoice_seq) || 1000;
  const next = seq + 1;
  const { data: claimed, error: claimErr } = await supabase
    .from("settings").update({ invoice_seq: next }).eq("id", 1).eq("invoice_seq", seq)
    .select("invoice_seq");
  if (claimErr) throw new Error(`Could not reserve an invoice number: ${claimErr.message}`);
  if (!claimed?.length) return claimInvoiceNumber(attempt + 1);

  return `HAL-${new Date().getFullYear()}-${next}`;
}

/**
 * Turn a request into a reservation.
 *
 * Matches the customer by email rather than creating a duplicate — a repeat
 * customer booking through the website should land on their existing record
 * with its history, not a second one.
 *
 * The rental is priced with the same autoPrice used everywhere else, so a
 * website booking and a phone booking for identical dates cost the same.
 */
export function useAcceptRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req) => {
      if (!req.equipment_id) {
        throw new Error(
          "This request has no machine attached, so it cannot be accepted automatically. " +
            "Create the rental by hand from the details below."
        );
      }

      const email = (req.email ?? "").trim().toLowerCase();
      if (!email) throw new Error("This request has no email address, which a customer record requires.");

      // 1. Find or create the customer.
      const { data: existing, error: findErr } = await supabase
        .from("customers").select("id").ilike("email", email).limit(1);
      if (findErr) throw new Error(`Could not look up the customer: ${findErr.message}`);

      let customerId = existing?.[0]?.id;
      let createdCustomer = false;
      if (!customerId) {
        const { data: made, error: makeErr } = await supabase
          .from("customers")
          .insert([{
            name: req.name || email,
            email,
            phone: req.phone || null,
            business: req.business || null,
            notes: "Created from a website booking request.",
          }])
          .select("id");
        if (makeErr) throw new Error(`Could not create the customer: ${makeErr.message}`);
        customerId = made[0].id;
        createdCustomer = true;
      }

      // 2. Price it exactly as the rest of the app would.
      const eq = req.equipment ?? {};
      const shape = {
        start_date: req.start_date, start_time: req.start_time,
        end_date: req.end_date, end_time: req.end_time,
      };
      const days = rentalDays(shape);
      const charge = autoPrice(days, eq.day_rate, eq.week_rate, eq.month_rate);

      // 3. Create the rental, flagged as coming from the website.
      const invoice_no = await claimInvoiceNumber();
      const { data: rental, error: rentErr } = await supabase
        .from("rentals")
        .insert([{
          equipment_id: req.equipment_id,
          customer_id: customerId,
          start_date: req.start_date,
          start_time: req.start_time || "09:00",
          end_date: req.end_date,
          end_time: req.end_time || "09:00",
          rental_charge: charge,
          total: rentalTotal({ rental_charge: charge }),
          status: "reserved",
          source: "website",
          invoice_no,
          notes: req.notes || null,
        }])
        .select("id,invoice_no");
      if (rentErr) throw new Error(`Could not create the rental: ${rentErr.message}`);

      // 4. Mark the request handled. Done last: if this fails the rental still
      //    exists, and a request that reappears is better than one silently lost.
      const { error: markErr } = await supabase
        .from("booking_requests").update({ processed: true }).eq("id", req.id);
      if (markErr) {
        throw new Error(
          `The reservation ${invoice_no} was created, but the request could not be marked as handled ` +
            `(${markErr.message}). Do not accept it twice.`
        );
      }

      return { invoiceNo: rental[0].invoice_no, createdCustomer, days, charge };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["booking_requests"] });
      qc.invalidateQueries({ queryKey: ["rentals"] });
      qc.invalidateQueries({ queryKey: ["customers"] });
      qc.invalidateQueries({ queryKey: ["settings"] });
    },
  });
}

/** Dismiss without booking — a duplicate, a test, or someone who called instead. */
export function useDismissRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const { data, error } = await supabase
        .from("booking_requests").update({ processed: true }).eq("id", id).select("id");
      if (error) throw new Error(error.message);
      if (!data?.length) throw new Error("Nothing changed — a security policy may have blocked it.");
      return id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["booking_requests"] }),
  });
}
